import {
  VpFormControl,
  VpFormLabel,
  VpSelect,
  VpSelectOption,
} from '@vtmn-play/react/headless';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserNeedBenefit } from '../CreateUserNeedForm';

import { FormCreateUserNeedResponseProductBenefitsItem } from '@spo/types';
import './user-need-benefits.scss';

export type UserNeedBenefitsProps = {
  availableProductBenefits: FormCreateUserNeedResponseProductBenefitsItem[];
  setUnBenefits: (benefits: UserNeedBenefit[]) => void;
};

export const emptyWholeBenefit: UserNeedBenefit = {
  id: 0,
  label: '',
  notation: '',
};

export const benefitNotationValues = ['1', '2', '3', '4', '5', 'YES', 'NO'];

export const UserNeedBenefits = ({
  availableProductBenefits,
  setUnBenefits,
}: UserNeedBenefitsProps) => {
  const { t } = useTranslation();
  const [wholeBenefits, setWholeBenefits] = useState<UserNeedBenefit[]>([
    emptyWholeBenefit,
  ]);

  const updateBenefit = (index: number, benefitShortLabel: string) => {
    const newBenefits = [...wholeBenefits].map((bene, beneIndex) =>
      beneIndex === index
        ? {
            id:
              availableProductBenefits.find(
                (benefit) =>
                  benefit.shortTechnicalLabel ===
                  benefitShortLabel.split(' - ')[1],
              )?.id || bene.id,
            label: benefitShortLabel,
            notation: '1',
          }
        : bene,
    );
    setWholeBenefits(newBenefits);
    setUnBenefits(newBenefits.filter((bn) => !!bn.id));
  };

  const updateNotation = (index: number, value: string) => {
    const newBenefits = [...wholeBenefits].map((bene, beneIndex) => ({
      ...bene,
      notation: beneIndex === index ? value : bene.notation,
    }));
    setWholeBenefits(newBenefits);
    setUnBenefits(newBenefits.filter((bn) => !!bn.id));
  };

  useEffect(() => {
    if (
      wholeBenefits.length < 8 &&
      !!wholeBenefits[wholeBenefits.length - 1].notation &&
      availableProductBenefits.length > wholeBenefits.length
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setWholeBenefits([...wholeBenefits, emptyWholeBenefit]);
    }
  }, [wholeBenefits, setWholeBenefits]);

  return (
    <div className="user-need-benefits" data-testid="user-need_benefits">
      <div className="user-need-benefit-list">
        {wholeBenefits.map((benefit, index) => (
          <div
            className="user-need-benefit-with-notation"
            data-testid="user-need_benefit_with_notation"
            key={`${benefit.label}-${index}`}
          >
            <VpFormControl>
              <VpFormLabel className="user-need-form-labels">
                {`${t('architecture.drawer.user-need.product-benefit-list')} ${index + 1}`}
              </VpFormLabel>
              <VpSelect
                id={`un-benefit-select-${index}`}
                name={`un-benefit-select-${index}`}
                data-testid={`user-need_benefit_select_${index}`}
                placeholder={t(
                  'architecture.drawer.user-need.product-benefit-list-placeholder',
                )}
                className="un-benefit-select"
                value={benefit.label ? benefit.label : ''}
                onChange={(e) => updateBenefit(index, e.target.value)}
              >
                {availableProductBenefits.map((benefit) => (
                  <VpSelectOption
                    key={benefit.id}
                    disabled={wholeBenefits
                      .filter((bene) => !!bene.label)
                      .map((bene) => bene.label)
                      .includes(
                        `${benefit.id} - ${benefit.shortTechnicalLabel}`,
                      )}
                  >
                    {`${benefit.id} - ${benefit.shortTechnicalLabel}`}
                  </VpSelectOption>
                ))}
              </VpSelect>
            </VpFormControl>
            <VpFormControl disabled={!benefit.label}>
              <VpFormLabel className="user-need-form-labels">
                {t('architecture.drawer.user-need.notation-list')}
              </VpFormLabel>
              <VpSelect
                id={`un-benefit-notation-${index}`}
                name={`un-benefit-notation-${index}`}
                data-testid={`user-need_benefit_notation_${index}`}
                className="un-benefit-notation"
                value={benefit.notation}
                onChange={(e) => updateNotation(index, e.target.value)}
              >
                {benefitNotationValues.map((notation) => (
                  <VpSelectOption key={notation}>{notation}</VpSelectOption>
                ))}
              </VpSelect>
            </VpFormControl>
          </div>
        ))}
      </div>
    </div>
  );
};
